"use client"

import { useEffect, useRef, useState } from "react"
import { X, Upload, Trash2, Loader2, Paperclip, FileText, Calendar, Clock, Plus, Search } from "lucide-react"
import {
    createBriefing,
    updateBriefing,
    uploadBriefingAttachment,
    getBriefingAttachments,
    deleteBriefingAttachment,
    getTeamMembers,
    getBriefingModels,
    addBriefingModel,
    updateBriefingModel,
    removeBriefingModel,
    getMySaved3ds,
    getClient,
    getUserId,
} from "../API"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import BriefingSelect from "./BriefingSelect"
import { markBriefingSeen } from "./briefingNotifications"

interface Model {
    id: string
    name: string
    thumbnail: string
    path: string
}

interface Attachment {
    id: string
    kind: string
    file_url: string
    file_name: string
}

interface BriefingModel {
    id?: string
    model_id?: string
    model_name?: string
    model_thumb?: string
    model_path?: string
    collar?: string
    sleeve?: string
    sizes?: string
    quantity?: string
    fabric?: string
    observation?: string
    position?: number
}

interface Briefing {
    id: string
    title: string
    client_name?: string
    client_contact?: string
    vendedor_id?: string
    vendedor_name?: string
    designer_id?: string
    designer_name?: string
    status: string
    priority: string
    deadline?: string
    deadline_time?: string
    duration?: string
    all_day?: boolean
    description?: string
    collar?: string
    sleeve?: string
    sizes?: string
    quantity?: string
    fabric?: string
    model_id?: string
    model_name?: string
    model_thumb?: string
    models?: BriefingModel[]
}

const STATUS_OPTIONS = [
    { value: "pendente", label: "Pendente" },
    { value: "em_producao", label: "Em produção" },
    { value: "em_revisao", label: "Em revisão" },
    { value: "aprovacao_cliente", label: "Pendente aprovação do cliente" },
    { value: "falta_resposta", label: "Falta de resposta" },
    { value: "concluido", label: "Concluído" },
    { value: "pausado", label: "Pausado" },
    { value: "cancelado", label: "Cancelado" },
]

const VENDEDOR_STATUS_OPTIONS = [
    { value: "pendente", label: "Pendente" },
    { value: "aprovacao_cliente", label: "Pendente aprovação do cliente" },
    { value: "falta_resposta", label: "Falta de resposta" },
    { value: "pausado", label: "Pausado" },
    { value: "cancelado", label: "Cancelado" },
]

const PRIORITY_OPTIONS = [
    { value: "baixa", label: "Baixa" },
    { value: "normal", label: "Normal" },
    { value: "alta", label: "Alta" },
    { value: "urgente", label: "Urgente" },
]

const DURATION_OPTIONS = ["30 min", "1 hora", "2 horas", "4 horas", "8 horas", "1 dia", "2 dias", "3 dias", "1 semana"]

const ATTACHMENT_KINDS = [
    { value: "logo", label: "Logos", accept: "image/*" },
    { value: "patrocinador", label: "Patrocinadores", accept: "image/*" },
    { value: "referencia", label: "Referências", accept: "image/*" },
    { value: "outro", label: "Arquivos diversos", accept: "*/*" },
]

const isImageFile = (name?: string) => !!name && /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(name)

interface Props {
    // Quando aberto a partir do 3-pontinhos de uma modelagem
    model?: Model | null
    // Quando aberto em modo edição, a partir da tela de briefings
    briefing?: Briefing | null
    onClose: () => void
    onSaved?: (briefing: any) => void
}

export default function BriefingModal({ model, briefing, onClose, onSaved }: Props) {
    // currentBriefing começa igual ao prop, mas passa a existir também
    // logo após a criação, para liberar o upload de anexos sem fechar o modal.
    const [currentBriefing, setCurrentBriefing] = useState<Briefing | null | undefined>(briefing)
    const isEditing = !!currentBriefing
    const [userRole, setUserRole] = useState<string>("")
    const isVendedor = userRole === "vendedor"

    useEffect(() => {
        getClient(getUserId()).then((c) => {
            if (c?.[0]?.role) setUserRole(c[0].role)
        })
    }, [])

    const [saving, setSaving] = useState(false)
    const [attachments, setAttachments] = useState<Attachment[]>([])
    const [uploadingKind, setUploadingKind] = useState<string | null>(null)
    const [designers, setDesigners] = useState<{ id: string; name: string; email: string }[]>([])
    const [vendedores, setVendedores] = useState<{ id: string; name: string; email: string }[]>([])
    const [models, setModels] = useState<BriefingModel[]>([])
    const [showModelPicker, setShowModelPicker] = useState(false)
    const [availableModels, setAvailableModels] = useState<any[]>([])
    const [modelSearchTerm, setModelSearchTerm] = useState("")

    useEffect(() => {
        Promise.all([
            getTeamMembers("designer"),
            getTeamMembers("vendedor"),
            getTeamMembers("gestao"),
        ]).then(([d, v, g]) => {
            const gestores = g || []
            setDesigners([...(d || []), ...gestores])
            setVendedores([...(v || []), ...gestores])
        })
    }, [])

    useEffect(() => {
        if (currentBriefing?.id) {
            getBriefingModels(currentBriefing.id).then((data) => {
                if (data?.length) setModels(data)
            })
        } else if (model) {
            setModels([{
                model_id: model.id,
                model_name: model.name,
                model_thumb: model.thumbnail,
                model_path: model.path,
                collar: briefing?.collar || "",
                sleeve: briefing?.sleeve || "",
                sizes: briefing?.sizes || "",
                quantity: briefing?.quantity || "",
                fabric: briefing?.fabric || "",
            }])
        }
    }, [currentBriefing?.id])

    useEffect(() => {
        if (showModelPicker && availableModels.length === 0) {
            getMySaved3ds().then((data) => {
                setAvailableModels(data || [])
            })
        }
    }, [showModelPicker])

    const filteredAvailableModels = availableModels.filter((m) => {
        const term = modelSearchTerm.toLowerCase()
        return !term || (m.name || "").toLowerCase().includes(term) || (m.category || "").toLowerCase().includes(term)
    })

    const addModelFromPicker = (m: any) => {
        const thumb = m.thumb2
            ? (m.path?.includes("models/novos/") ? m.thumb2 : `https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/${m.thumb2}`)
            : ""
        const glb = m.path?.includes("models/novos/") ? m.path : `https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/3ds/${m.path}.glb`
        setModels(prev => [...prev, {
            model_id: m.id,
            model_name: m.name,
            model_thumb: thumb,
            model_path: glb,
            collar: "",
            sleeve: "",
            sizes: "",
            quantity: "",
            fabric: "",
        }])
        setShowModelPicker(false)
        setModelSearchTerm("")
    }

    const [form, setForm] = useState({
        title: briefing?.title || (model ? `Briefing - ${model.name}` : ""),
        client_name: briefing?.client_name || "",
        client_contact: briefing?.client_contact || "",
        vendedor_id: briefing?.vendedor_id || "",
        vendedor_name: briefing?.vendedor_name || "",
        designer_id: briefing?.designer_id || "",
        designer_name: briefing?.designer_name || "",
        status: briefing?.status || "pendente",
        priority: briefing?.priority || "normal",
        deadline: briefing?.deadline || "",
        deadline_time: briefing?.deadline_time || "",
        duration: briefing?.duration || "8 horas",
        all_day: briefing?.all_day !== undefined ? briefing.all_day : true,
        description: briefing?.description || "",
    })

    useEffect(() => {
        if (isVendedor && !form.vendedor_id) {
            setForm((prev) => ({
                ...prev,
                vendedor_id: getUserId() || "",
                vendedor_name: localStorage.getItem("user_name") || "",
            }))
        }
    }, [isVendedor])

    useEffect(() => {
        if (currentBriefing?.id) {
            getBriefingAttachments(currentBriefing.id).then((data) => setAttachments(data || []))
        }
    }, [currentBriefing?.id])

    const update = (key: string, value: string | boolean) => setForm((prev) => ({ ...prev, [key]: value }))
    const dateInputRef = useRef<HTMLInputElement>(null)

    // Salva os dados do briefing. Na primeira vez (criação) mantém o modal
    // aberto para permitir anexar logos/patrocinadores na hora.
    const handleSave = async (closeAfter: boolean) => {
        if (!form.title.trim()) {
            alert("Informe um título para o briefing.")
            return
        }
        if (!form.client_name.trim()) {
            alert("Informe o nome do cliente.")
            return
        }
        if (!form.client_contact.trim()) {
            alert("Informe o contato do cliente.")
            return
        }
        if (!form.vendedor_id) {
            alert("Selecione um vendedor.")
            return
        }
        if (!models.length) {
            alert("Adicione pelo menos uma modelagem ao briefing.")
            return
        }
        setSaving(true)
        try {
            if (currentBriefing) {
                const result = await updateBriefing(currentBriefing.id, form)
                if (result) {
                    const existingModels = await getBriefingModels(currentBriefing.id)
                    const existingIds = new Set(existingModels.map((m: any) => m.id))
                    const incomingIds = new Set(models.filter(m => m.id).map(m => m.id))

                    for (const existing of existingModels) {
                        if (!incomingIds.has(existing.id)) {
                            await removeBriefingModel(existing.id)
                        }
                    }

                    for (let i = 0; i < models.length; i++) {
                        const m = models[i]
                        const payload = { ...m, position: i }
                        delete payload.id
                        if (m.id && existingIds.has(m.id)) {
                            await updateBriefingModel(m.id, payload)
                        } else {
                            await addBriefingModel(currentBriefing.id, payload)
                        }
                    }

                    setCurrentBriefing(result)
                    markBriefingSeen(result.id)
                    onSaved?.(result)
                }
            } else {
                const result = await createBriefing({
                    ...form,
                    models: models.map((m, i) => ({ ...m, position: i })),
                })
                if (result) { setCurrentBriefing(result); markBriefingSeen(result.id); onSaved?.(result) }
            }
            if (closeAfter) onClose()
        } finally {
            setSaving(false)
        }
    }

    const handleAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>, kind: string) => {
        const files = Array.from(e.target.files || [])
        e.target.value = ""
        if (!files.length || !currentBriefing?.id) return
        setUploadingKind(kind)
        try {
            const results = await Promise.all(
                files.map((file) => uploadBriefingAttachment(currentBriefing.id, file, kind))
            )
            const saved = results.filter(Boolean)
            if (saved.length) setAttachments((prev) => [...prev, ...saved])
        } finally {
            setUploadingKind(null)
        }
    }

    const handleAttachmentDelete = async (att: Attachment) => {
        const ok = await deleteBriefingAttachment(att.id, (att as any).file_path)
        if (ok) setAttachments((prev) => prev.filter((a) => a.id !== att.id))
    }

    return (
        <>
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60">
            <div className="w-[640px] max-h-[90vh] overflow-y-auto rounded-xl bg-background p-6 shadow-2xl">
                <div className="flex items-center justify-between mb-5">
                    <h2 className="text-lg font-semibold text-primary">
                        {isEditing ? "Editar Briefing" : "Criar Briefing"}
                    </h2>
                    <button onClick={onClose} className="rounded-full p-1 hover:bg-secondary transition-colors">
                        <X className="h-5 w-5 text-primary" />
                    </button>
                </div>

                {/* Modelagens vinculadas */}
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                        <Label className="text-primary">Modelagens ({models.length})</Label>
                        <button
                            type="button"
                            onClick={() => setShowModelPicker(true)}
                            className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                        >
                            <Plus className="h-3.5 w-3.5" /> Adicionar modelagem
                        </button>
                    </div>
                    {models.length === 0 ? (
                        <p className="text-xs text-primary/40 bg-secondary rounded-md p-3">
                            Nenhuma modelagem vinculada. Clique em "Adicionar modelagem" ou selecione uma modelagem para iniciar.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {models.map((m, idx) => (
                                <div key={idx} className="bg-secondary rounded-lg p-3 border border-white/5">
                                    <div className="flex items-center gap-3 mb-2">
                                        {m.model_thumb ? (
                                            <img src={m.model_thumb} alt={m.model_name} className="h-10 w-10 rounded object-contain bg-black/20" />
                                        ) : (
                                            <div className="h-10 w-10 rounded bg-black/20 flex items-center justify-center text-[10px] text-primary/30">3D</div>
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <input
                                                className="text-sm font-medium text-primary bg-transparent border-0 p-0 focus:outline-none w-full"
                                                value={m.model_name || ""}
                                                onChange={(e) => {
                                                    const updated = [...models]
                                                    updated[idx] = { ...updated[idx], model_name: e.target.value }
                                                    setModels(updated)
                                                }}
                                                placeholder="Nome da modelagem"
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setModels(prev => prev.filter((_, i) => i !== idx))}
                                            className="p-1 rounded hover:bg-red-500/20 transition-colors"
                                        >
                                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div>
                                            <Label className="text-primary/60 text-[10px]">Escudo *</Label>
                                            <BriefingSelect
                                                value={m.collar || ""}
                                                onChange={(v) => {
                                                    const updated = [...models]
                                                    updated[idx] = { ...updated[idx], collar: v }
                                                    setModels(updated)
                                                }}
                                                placeholder="Selecionar..."
                                                options={[
                                                    { value: "Bordado", label: "Bordado" },
                                                    { value: "Sublimado", label: "Sublimado" },
                                                    { value: "DTF", label: "DTF" },
                                                ]}
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-primary/60 text-[10px]">Manga</Label>
                                            <Input className="text-primary text-xs" value={m.sleeve || ""}
                                                onChange={(e) => {
                                                    const updated = [...models]
                                                    updated[idx] = { ...updated[idx], sleeve: e.target.value }
                                                    setModels(updated)
                                                }} placeholder="Curta" />
                                        </div>
                                        <div>
                                            <Label className="text-primary/60 text-[10px]">Tecido</Label>
                                            <Input className="text-primary text-xs" value={m.fabric || ""}
                                                onChange={(e) => {
                                                    const updated = [...models]
                                                    updated[idx] = { ...updated[idx], fabric: e.target.value }
                                                    setModels(updated)
                                                }} placeholder="Dry Fit" />
                                        </div>
                                        <div>
                                            <Label className="text-primary/60 text-[10px]">Tamanhos</Label>
                                            <Input className="text-primary text-xs" value={m.sizes || ""}
                                                onChange={(e) => {
                                                    const updated = [...models]
                                                    updated[idx] = { ...updated[idx], sizes: e.target.value }
                                                    setModels(updated)
                                                }} placeholder="P, M, G, GG" />
                                        </div>
                                        <div>
                                            <Label className="text-primary/60 text-[10px]">Quantidade</Label>
                                            <Input className="text-primary text-xs" value={m.quantity || ""}
                                                onChange={(e) => {
                                                    const updated = [...models]
                                                    updated[idx] = { ...updated[idx], quantity: e.target.value }
                                                    setModels(updated)
                                                }} placeholder="20" />
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        <Label className="text-primary/60 text-[10px]">Observação</Label>
                                        <textarea className="text-primary text-xs w-full rounded-md border border-input bg-background px-2 py-1.5 resize-none" rows={2} value={m.observation || ""}
                                            onChange={(e) => {
                                                const updated = [...models]
                                                updated[idx] = { ...updated[idx], observation: e.target.value }
                                                setModels(updated)
                                            }} placeholder="Ex: bordado dourado, logo grande..." />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Título */}
                <div className="mb-3">
                    <Label className="text-primary" htmlFor="briefing-title">Título</Label>
                    <Input
                        className="text-primary"
                        id="briefing-title"
                        value={form.title}
                        onChange={(e) => update("title", e.target.value)}
                        placeholder="Ex: PRIORIDADE 8 - Criar layout"
                    />
                </div>

                {/* Data + Hora + Duração + Dia inteiro */}
                <div className="flex items-end gap-3 mb-3">
                    <div className="flex-1">
                        <Label className="text-primary" htmlFor="briefing-deadline">Data</Label>
                        <div
                            className="relative flex items-center rounded-md border border-white/10 bg-secondary px-3 py-2 cursor-text"
                            onClick={() => (dateInputRef.current as any)?.showPicker ? (dateInputRef.current as any).showPicker() : dateInputRef.current?.focus()}
                        >
                            <span className="text-sm text-primary flex-1">
                                {form.deadline ? new Date(form.deadline + "T00:00:00").toLocaleDateString("pt-BR") : "Selecionar data"}
                            </span>
                            <Calendar className="h-4 w-4 text-primary/50 flex-shrink-0" />
                            <input
                                ref={dateInputRef}
                                id="briefing-deadline"
                                type="date"
                                value={form.deadline}
                                onChange={(e) => update("deadline", e.target.value)}
                                className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
                            />
                        </div>
                    </div>

                    {!form.all_day && (
                        <div className="flex-1">
                            <Label className="text-primary" htmlFor="briefing-time">Hora</Label>
                            <div className="relative flex items-center rounded-md border border-white/10 bg-secondary px-3 py-2">
                                <Input
                                    id="briefing-time" type="time"
                                    className="text-primary border-0 bg-transparent p-0 h-auto focus-visible:ring-0"
                                    value={form.deadline_time}
                                    onChange={(e) => update("deadline_time", e.target.value)}
                                />
                                <Clock className="h-4 w-4 text-primary/50 flex-shrink-0" />
                            </div>
                        </div>
                    )}

                    {!form.all_day && (
                        <div className="flex-1">
                            <Label className="text-primary">Duração</Label>
                            <BriefingSelect
                                value={form.duration}
                                onChange={(v) => update("duration", v)}
                                options={DURATION_OPTIONS.map((d) => ({ value: d, label: d }))}
                            />
                        </div>
                    )}

                    <label className="flex items-center gap-2 pb-2.5 whitespace-nowrap cursor-pointer select-none">
                        <input
                            type="checkbox"
                            checked={form.all_day}
                            onChange={(e) => update("all_day", e.target.checked)}
                            className="h-4 w-4 rounded border-white/20 accent-blue-600"
                        />
                        <span className="text-sm text-primary">Dia inteiro</span>
                    </label>
                </div>

                {/* Status + Prioridade */}
                {!isVendedor && (
                <div className="grid gap-3 mb-3 grid-cols-2">
                    <div>
                        <Label className="text-primary">Status</Label>
                        <BriefingSelect
                            value={form.status}
                            onChange={(v) => update("status", v)}
                            options={STATUS_OPTIONS}
                        />
                    </div>
                    <div>
                        <Label className="text-primary">Prioridade</Label>
                        <BriefingSelect
                            value={form.priority}
                            onChange={(v) => update("priority", v)}
                            options={PRIORITY_OPTIONS}
                        />
                    </div>
                </div>
                )}

                {/* Cliente + contato */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                        <Label className="text-primary" htmlFor="briefing-client">Cliente <span className="text-red-400">*</span></Label>
                        <Input className="text-primary" id="briefing-client" value={form.client_name}
                            onChange={(e) => update("client_name", e.target.value)} placeholder="Nome do cliente" />
                    </div>
                    <div>
                        <Label className="text-primary" htmlFor="briefing-contact">Contato <span className="text-red-400">*</span></Label>
                        <Input className="text-primary" id="briefing-contact" value={form.client_contact}
                            onChange={(e) => update("client_contact", e.target.value)} placeholder="WhatsApp / telefone" />
                    </div>
                </div>

                {/* Vendedor + Designer responsável (cadastrados em "Dados da Loja") */}
                <div className={`grid gap-3 mb-3 ${isVendedor ? "grid-cols-1" : "grid-cols-2"}`}>
                    {isVendedor ? (
                        <div>
                            <Label className="text-primary">Vendedor</Label>
                            <p className="text-sm text-primary bg-secondary rounded-md px-3 py-2.5">
                                {localStorage.getItem("user_name") || "Você"}
                            </p>
                        </div>
                    ) : (
                        <div>
                            <Label className="text-primary">Vendedor <span className="text-red-400">*</span></Label>
                            {vendedores.length > 0 ? (
                                <BriefingSelect
                                    value={form.vendedor_id}
                                    onChange={(v) => {
                                        const found = vendedores.find((d) => d.id === v)
                                        setForm((prev) => ({ ...prev, vendedor_id: v, vendedor_name: found?.name || "" }))
                                    }}
                                    options={vendedores.map((v) => ({ value: v.id, label: v.name }))}
                                    placeholder="Selecionar vendedor"
                                />
                            ) : (
                                <p className="text-xs text-primary/40 bg-secondary rounded-md p-2.5">
                                    Nenhum vendedor cadastrado ainda em "Dados da Loja".
                                </p>
                            )}
                        </div>
                    )}
                    <div>
                        <Label className="text-primary">Designer responsável</Label>
                        {designers.length > 0 ? (
                            <BriefingSelect
                                value={form.designer_id}
                                onChange={(v) => {
                                    const found = designers.find((d) => d.id === v)
                                    setForm((prev) => ({
                                        ...prev,
                                        designer_id: v,
                                        designer_name: found?.name || "",
                                    }))
                                }}
                                options={designers.map((d) => ({ value: d.id, label: d.name }))}
                                placeholder="Ainda não atribuído"
                            />
                        ) : (
                            <p className="text-xs text-primary/40 bg-secondary rounded-md p-2.5">
                                Nenhum designer cadastrado ainda em "Dados da Loja".
                            </p>
                        )}
                    </div>
                </div>

                {/* Observação / briefing */}
                <div className="mb-4">
                    <Label className="text-primary" htmlFor="briefing-desc">Observação</Label>
                    <textarea
                        id="briefing-desc"
                        className="w-full rounded-md border border-white/10 bg-secondary px-3 py-2 text-sm text-primary min-h-[100px] focus:outline-none focus:border-blue-500"
                        value={form.description}
                        onChange={(e) => update("description", e.target.value)}
                        placeholder="Detalhes do que o cliente pediu: cores, referências, número na frente, etc."
                    />
                </div>

                {/* Anexos: logos, patrocinadores, referências, diversos */}
                <div className="mb-4">
                    <Label className="text-primary mb-2 block">Anexos</Label>
                    {!isEditing ? (
                        <p className="text-xs text-primary/50 bg-secondary rounded-md p-3">
                            Clique em "Salvar e continuar" para liberar o upload de anexos.
                        </p>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-2 mb-3">
                                {ATTACHMENT_KINDS.map((k) => (
                                    <label
                                        key={k.value}
                                        className="flex items-center gap-1.5 rounded-lg border border-dashed border-white/20 px-3 py-2 text-xs text-primary/60 hover:text-primary hover:border-white/40 cursor-pointer transition-colors"
                                    >
                                        {uploadingKind === k.value
                                            ? <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
                                            : <Upload className="h-3.5 w-3.5 flex-shrink-0" />}
                                        {k.label}
                                        <span className="text-primary/30">(várias)</span>
                                        <input
                                            type="file" accept={k.accept} multiple
                                            className="hidden"
                                            onChange={(e) => handleAttachmentUpload(e, k.value)}
                                        />
                                    </label>
                                ))}
                            </div>
                            {attachments.length > 0 && (
                                <div className="grid grid-cols-4 gap-2">
                                    {attachments.map((att) => (
                                        <div key={att.id} className="relative group rounded-lg border border-white/10 bg-secondary p-2">
                                            {isImageFile(att.file_name) ? (
                                                <img src={att.file_url} alt={att.file_name} className="h-16 w-full object-contain" />
                                            ) : (
                                                <div className="h-16 w-full flex flex-col items-center justify-center gap-1 text-primary/50">
                                                    <FileText className="h-6 w-6" />
                                                </div>
                                            )}
                                            <p className="text-[10px] text-primary/50 truncate mt-1">{att.file_name}</p>
                                            <p className="text-[9px] text-primary/30 truncate">{ATTACHMENT_KINDS.find(k => k.value === att.kind)?.label || att.kind}</p>
                                            <button
                                                onClick={() => handleAttachmentDelete(att)}
                                                className="absolute top-1 right-1 rounded-full bg-black/50 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                <Trash2 className="h-3 w-3 text-red-400" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="rounded-lg border border-white/10 px-4 py-2 text-sm text-primary/70 hover:text-primary transition-colors">
                        Cancelar
                    </button>
                    {!isEditing && (
                        <button
                            onClick={() => handleSave(false)}
                            disabled={saving}
                            className="flex items-center gap-2 rounded-lg border border-white/10 px-4 py-2 text-sm font-medium text-primary hover:bg-white/5 transition-colors disabled:opacity-60"
                        >
                            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                            Salvar e continuar
                        </button>
                    )}
                    <button
                        onClick={() => handleSave(true)}
                        disabled={saving}
                        className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-60"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        {isEditing ? "Salvar e fechar" : "Criar Briefing"}
                    </button>
                </div>
            </div>
        </div>

        {showModelPicker && (
            <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/70">
                <div className="w-[560px] max-h-[80vh] overflow-hidden rounded-xl bg-background p-5 shadow-2xl flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-semibold text-primary">Selecionar Modelagem</h3>
                        <button onClick={() => { setShowModelPicker(false); setModelSearchTerm("") }} className="rounded-full p-1 hover:bg-secondary transition-colors">
                            <X className="h-4 w-4 text-primary" />
                        </button>
                    </div>
                    <div className="relative mb-3">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-primary/40" />
                        <input
                            type="text"
                            value={modelSearchTerm}
                            onChange={(e) => setModelSearchTerm(e.target.value)}
                            placeholder="Buscar por nome ou categoria..."
                            className="w-full rounded-md border border-white/10 bg-secondary pl-8 pr-3 py-2 text-sm text-primary placeholder:text-primary/30 focus:outline-none focus:border-blue-500"
                            autoFocus
                        />
                    </div>
                    <div className="flex-1 overflow-y-auto min-h-0">
                        {filteredAvailableModels.length === 0 ? (
                            <p className="text-center text-sm text-primary/40 py-8">Nenhuma modelagem encontrada.</p>
                        ) : (
                            <div className="grid grid-cols-3 gap-2">
                                {filteredAvailableModels.map((m) => {
                                    const thumb = m.thumb2
                                        ? (m.path?.includes("models/novos/") ? m.thumb2 : `https://nfghuenhuaecfyccyaib.supabase.co/storage/v1/object/public/images/${m.thumb2}`)
                                        : ""
                                    return (
                                        <button
                                            key={m.id}
                                            onClick={() => addModelFromPicker(m)}
                                            className="flex flex-col items-center rounded-lg border border-white/10 bg-white/[0.03] p-2 hover:border-blue-500/50 hover:bg-white/[0.06] transition-colors text-left"
                                        >
                                            {thumb ? (
                                                <img src={thumb} alt={m.name} className="h-20 w-full rounded object-contain bg-black/20 mb-1.5" />
                                            ) : (
                                                <div className="h-20 w-full rounded bg-black/20 mb-1.5 flex items-center justify-center text-[10px] text-primary/30">3D</div>
                                            )}
                                            <p className="text-[11px] text-primary font-medium text-center line-clamp-2 w-full">{m.name}</p>
                                        </button>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )}
        </>
    )
}